import { BLOG_TAG_PAGES } from '@/data/blogTagPages.generated.js';
import { BLOG_TAG_POSTS } from '@/data/blogTagPosts.generated.js';
import { relatedBlogTags } from '@/lib/blogTags.shared.js';
import {
  getRelatedServiceSlugsByEntitySlug,
  getServiceBySlug,
  listServiceDefinitions,
  localizeServiceText,
} from '@/lib/serviceCatalog';

type Locale = 'tr' | 'en';
type L = { tr: string; en: string };

export type BlogTagPageEntry = {
  slug: string;
  label: L;
  aliases: string[];
  postSlugs: string[];
  postCount: number;
  locales: Locale[];
  indexable: boolean;
  lastmod: string;
};

export type BlogTagPostSummary = {
  slug: string;
  source: 'static' | 'cms';
  publishedAt: string;
  title: L;
  excerpt: L;
  category: L;
  readingMinutes: { tr: number; en: number };
  headings: { tr: string[]; en: string[] };
  locales: Locale[];
  tags: string[];
};

const pages = BLOG_TAG_PAGES as BlogTagPageEntry[];
const postsBySlug = new Map((BLOG_TAG_POSTS as BlogTagPostSummary[]).map((post) => [post.slug, post]));

/** Keyword → service hints for tags that no post-level service mapping covers. */
const TAG_SERVICE_RULES: Array<{ pattern: RegExp; services: string[] }> = [
  { pattern: /(^|-)(ai|llm|chatgpt|openai|evaluation|drift|governance|assistant)(-|$)/, services: ['ai-development', 'ai-automation-agents'] },
  { pattern: /(mobile|mobil|react-native|jsi|ios|offline|sync|conflict)/, services: ['mobile-app-development'] },
  { pattern: /(saas|multi-tenancy)/, services: ['saas-mvp-development'] },
  { pattern: /(mvp|startup)/, services: ['saas-mvp-development', 'startup-product-engineering'] },
  { pattern: /(^|-)ux(-|$)|design/, services: ['ui-ux-design'] },
  { pattern: /(frontend|react|streaming|suspense|state)/, services: ['web-development'] },
  {
    pattern: /(backend|api|architecture|distributed|edge|latency|nodejs|event-loop|serverless|observability|sre|cloud|security|reliability|schema|compatibility|deprecation|tech-debt|yazilim|software|engineering|testing|release|canary|rollback|delivery|progressive|ownership|developer|leadership|system)/,
    services: ['custom-software-development'],
  },
];

const TOOL_LINKS: Record<string, L> = {
  'project-cost-estimator': { tr: 'Yazılım Proje Maliyet Tahmincisi', en: 'Software Project Cost Estimator' },
  'pitch-evaluator': { tr: 'AI Yatırımcı Pitch Değerlendirici', en: 'AI Investor Pitch Evaluator' },
  'roi-calculator': { tr: 'Dijital Pazarlama ROI Hesaplayıcı', en: 'Digital Marketing ROI Calculator' },
};

export function getBlogTagPageEntry(slug: string): BlogTagPageEntry | null {
  return pages.find((page) => page.slug === slug) ?? null;
}

function formatMonth(iso: string, locale: Locale) {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale === 'tr' ? 'tr-TR' : 'en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function joinList(items: string[], locale: Locale) {
  if (items.length <= 1) return items.join('');
  const last = items[items.length - 1];
  return `${items.slice(0, -1).join(', ')} ${locale === 'tr' ? 've' : 'and'} ${last}`;
}

function truncate(text: string, max: number) {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), max - 20)).replace(/[\s,;:—-]+$/, '')}…`;
}

function rankServices(entry: BlogTagPageEntry): string[] {
  const score = new Map<string, number>();
  const add = (slug: string, points: number) => {
    if (getServiceBySlug(slug)) score.set(slug, (score.get(slug) ?? 0) + points);
  };
  for (const postSlug of entry.postSlugs) {
    for (const slug of getRelatedServiceSlugsByEntitySlug(postSlug)) add(slug, 2);
    for (const service of listServiceDefinitions()) {
      if (service.relatedBlogSlugs.includes(postSlug)) add(service.slug, 2);
    }
  }
  for (const rule of TAG_SERVICE_RULES) {
    if (rule.pattern.test(entry.slug)) rule.services.forEach((slug) => add(slug, 3));
  }
  const ranked = [...score.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([slug]) => slug);
  return (ranked.length ? ranked : ['custom-software-development']).slice(0, 3);
}

export function buildBlogTagPageModel(slug: string, locale: Locale) {
  const entry = getBlogTagPageEntry(slug);
  if (!entry || !entry.locales.includes(locale)) return null;

  const label = entry.label[locale];
  const posts = entry.postSlugs
    .map((postSlug) => postsBySlug.get(postSlug))
    .filter((post): post is BlogTagPostSummary => Boolean(post && post.locales.includes(locale)));
  if (!posts.length) return null;

  const count = posts.length;
  const dates = posts.map((post) => post.publishedAt).filter(Boolean).sort();
  const first = dates[0];
  const last = dates[dates.length - 1];
  const totalMinutes = posts.reduce((sum, post) => sum + (post.readingMinutes[locale] || 0), 0);

  const categoryCounts = new Map<string, number>();
  for (const post of posts) {
    const category = post.category[locale];
    if (category) categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
  }
  const categories = [...categoryCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, n]) => ({ name, count: n }));

  const relatedTags = relatedBlogTags(entry.slug, pages, 8)
    .filter((tag) => tag.locales.includes(locale))
    .map((tag) => ({ slug: tag.slug, label: tag.label[locale], shared: tag.shared, indexable: tag.indexable }));

  const services = rankServices(entry)
    .map((serviceSlug) => getServiceBySlug(serviceSlug))
    .filter((service): service is NonNullable<ReturnType<typeof getServiceBySlug>> => Boolean(service))
    .map((service) => ({
      slug: service.slug,
      title: localizeServiceText(service.navTitle, locale),
      intro: localizeServiceText(service.intro, locale),
      toolIds: service.relatedToolIds,
    }));

  const toolIds = [...new Set(['project-cost-estimator', ...services.flatMap((service) => service.toolIds)])];
  if (/(mvp|startup|saas)/.test(entry.slug)) toolIds.push('pitch-evaluator');
  const tools = [...new Set(toolIds)]
    .filter((id) => TOOL_LINKS[id])
    .slice(0, 3)
    .map((id) => ({ id, title: TOOL_LINKS[id][locale] }));

  const topics = posts
    .flatMap((post) => post.headings[locale].slice(0, 3).map((heading) => ({ heading, post })))
    .slice(0, 10);

  const latest = posts[0];
  const longest = [...posts].sort(
    (a, b) => (b.readingMinutes[locale] || 0) - (a.readingMinutes[locale] || 0) || a.slug.localeCompare(b.slug),
  )[0];
  const topRelated = relatedTags.slice(0, 3).map((tag) => tag.label);
  const categoryNames = categories.slice(0, 2).map((category) => category.name);
  const serviceNames = services.map((service) => service.title);

  const period =
    first && last && first !== last
      ? locale === 'tr'
        ? `${formatMonth(first, locale)} – ${formatMonth(last, locale)} arasında`
        : `between ${formatMonth(first, locale)} and ${formatMonth(last, locale)}`
      : first
        ? locale === 'tr'
          ? `${formatMonth(first, locale)} tarihinde`
          : `in ${formatMonth(first, locale)}`
        : '';

  const intro =
    locale === 'tr'
      ? [
          `Figtures blogunda “${label}” etiketiyle ${count} yazı ${period} yayımlandı; toplam okuma süresi yaklaşık ${totalMinutes} dakika.`,
          categoryNames.length
            ? `Yazılar ağırlıklı olarak ${joinList(categoryNames, locale)} kategorisinde${topRelated.length ? ` ve sıklıkla ${joinList(topRelated, locale)} konularıyla birlikte ele alınıyor` : ''}.`
            : '',
        ]
      : [
          `Figtures has published ${count} ${count === 1 ? 'article' : 'articles'} tagged “${label}” ${period}, about ${totalMinutes} minutes of reading in total.`,
          categoryNames.length
            ? `Most of them sit in the ${joinList(categoryNames, locale)} ${categoryNames.length > 1 ? 'categories' : 'category'}${topRelated.length ? ` and are often read together with ${joinList(topRelated, locale)}` : ''}.`
            : '',
        ];

  const faq =
    locale === 'tr'
      ? [
          {
            question: `${label} konusunda hangi yazıyla başlamalıyım?`,
            answer:
              count > 1 && longest.slug !== latest.slug
                ? `En güncel yazı “${latest.title.tr}”; en kapsamlısı ise yaklaşık ${longest.readingMinutes.tr} dakikalık “${longest.title.tr}”.`
                : `“${latest.title.tr}” ile başlayın: ${latest.excerpt.tr}`,
          },
          ...(topRelated.length
            ? [
                {
                  question: `${label} ile birlikte hangi konular okunuyor?`,
                  answer: `Bu etiketteki yazılar en çok ${joinList(topRelated, locale)} etiketleriyle kesişiyor; ilgili etiket sayfaları aşağıda.`,
                },
              ]
            : []),
          {
            question: `Figtures ${label} alanında hangi hizmeti veriyor ve fiyat nasıl belirleniyor?`,
            answer: `İlgili hizmetler: ${joinList(serviceNames, locale)}. Fiyat proje bazında belirlenir: ihtiyacı dinleyip görev listesini çıkarırız, efor hesabına göre projeye uygun outsource ekip kurulur ve fiyat bu efor/maliyetten hesaplanarak teklif dokümanıyla paylaşılır.`,
          },
        ]
      : [
          {
            question: `Which ${label} article should I read first?`,
            answer:
              count > 1 && longest.slug !== latest.slug
                ? `The newest is “${latest.title.en}”; the most in-depth is “${longest.title.en}” at about ${longest.readingMinutes.en} minutes.`
                : `Start with “${latest.title.en}”: ${latest.excerpt.en}`,
          },
          ...(topRelated.length
            ? [
                {
                  question: `Which topics are read together with ${label}?`,
                  answer: `Articles under this tag overlap most with ${joinList(topRelated, locale)}; those tag pages are linked below.`,
                },
              ]
            : []),
          {
            question: `Which Figtures services cover ${label}, and how is pricing set?`,
            answer: `Related services: ${joinList(serviceNames, locale)}. Pricing is set per project: we listen to the need, turn it into a task list, estimate the effort, assemble an outsourced team for the project, and price the work from that effort and cost in a written proposal.`,
          },
        ];

  const titles = posts.map((post) => post.title[locale]);
  const title = locale === 'tr' ? `${label} yazıları ve rehberleri` : `${label} articles and guides`;
  const description = truncate(
    locale === 'tr'
      ? `${label} üzerine ${count} yazı: ${titles.join('; ')}.`
      : `${count} ${count === 1 ? 'article' : 'articles'} on ${label}: ${titles.join('; ')}.`,
    158,
  );

  return {
    entry,
    label,
    title,
    description,
    intro: intro.filter(Boolean),
    posts,
    count,
    first,
    last,
    totalMinutes,
    categories,
    relatedTags,
    services,
    tools,
    topics,
    faq,
  };
}
